import { useEffect, useMemo, useState } from "react";
import { HitInlineLoader } from "@/components/loading/HitInlineLoader";
import { HitSkeletonGrid } from "@/components/loading/HitSkeletonCard";
import { versionOpts } from "@/components/publish/SearchResultsP2";
import { rarityTier, TIER_CHIP } from "@/lib/rarityTier";
import { makeCardImgFallback } from "@/lib/imageFallback";
import { SearchBox, imgUrl } from "./ui";

const Filter = ({ label, value, options, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`h-8 max-w-[118px] appearance-none rounded-full border px-3 text-xs bg-[#101010] ${value ? "border-[#FF4438] text-white" : "border-white/10 text-white/70"}`}>
    <option value="">{label} ▾</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

const useHeaderTop = () => {
  const [h, setH] = useState(64);
  useEffect(() => {
    const el = document.querySelector('[data-testid="site-header"]');
    if (!el) { return undefined; }
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, []);
  return h;
};

export const ResultRow = ({ c, game, onPick, best, kbActive, kbVersion, compact }) => {
  const meta = [c.set_name, c.local_id ? `#${c.local_id}` : null].filter(Boolean).join(" · ");
  const sub = [c.year, (c.languages || []).map((l) => l.toUpperCase()).join(" ")].filter(Boolean).join(" · ");
  return (
    <li className={`flex gap-3 p-2.5 rounded-[14px] border bg-[#101010] ${kbActive ? "ring-2 ring-white/60" : ""}`} style={{ borderColor: best ? "#FF4438" : "rgba(255,255,255,.1)" }}
      data-testid={`p2-result-${c.id}`} data-kb-active={kbActive ? "true" : undefined} ref={(el) => { if (kbActive && el) el.scrollIntoView({ block: "nearest" }); }}>
      <button type="button" onClick={() => onPick(c, {})} className="w-11 h-[60px] shrink-0 rounded-md overflow-hidden bg-black grid place-items-center" data-testid={`p2-pick-${c.id}`}>
        {imgUrl(c.image_url) ? <img src={imgUrl(c.image_url)} alt={c.name} loading="lazy" className="w-full h-full object-contain" onError={makeCardImgFallback((img) => { img.style.display = "none"; })} />
          : <span className="text-[8px] text-white/30">Sin imagen</span>}
      </button>
      <button type="button" onClick={() => onPick(c, {})} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[14px] font-bold text-white truncate">{c.name}</span>
          {c.rarity && !compact && <span className={`sx-mono text-[8px] uppercase px-1 py-px rounded border ${TIER_CHIP[rarityTier(c.rarity)]}`}>{c.rarity}</span>}
        </div>
        {meta && <div className="text-[12px] text-white/55 truncate">{meta}</div>}
        {sub && !compact && <div className="sx-mono text-[9.5px] text-white/40">{sub}</div>}
        {c.first_edition_available && (
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onPick(c, { variant: "normal", first_edition: true, label: "1.ª edición" }); }}
            className="text-[11px] text-[#fbbf24] underline" data-testid={`p2-first-edition-${c.id}`}>¿Es 1.ª edición? Indícalo aquí</span>
        )}
      </button>
      {c.versions?.length > 0 && (
        <div className={`flex ${compact ? "flex-row items-center" : "flex-col"} gap-1.5 shrink-0 justify-center`}>
          {c.versions.slice(0, 3).map((v, i) => (
            <button key={v.key} type="button" onClick={() => onPick(c, versionOpts(game, v))} data-testid={`p2-version-${c.id}-${v.key}`}
              className={`h-8 px-3 max-w-[110px] truncate rounded-[10px] border text-[11.5px] font-semibold text-white ${kbActive && kbVersion === i ? "bg-[#FF4438] border-[#FF4438] text-[#080808]" : "border-white/15 hover:border-[#FF4438]"}`}>{v.label}</button>
          ))}
          {c.versions.length > 3 && <button type="button" onClick={() => onPick(c, {})} className="h-8 px-3 rounded-[10px] border border-white/15 text-[11.5px] text-white/70" data-testid={`p2-more-versions-${c.id}`}>+{c.versions.length - 3}</button>}
        </div>
      )}
    </li>
  );
};

// P2 · Resultados · más coincidencias, más datos
export const P2Resultados = ({ s, game, onPick, onNewCard, onClear, kb, onScan }) => {
  const [f, setF] = useState({ set: "", rarity: "", year: "" });
  const top = useHeaderTop();
  const opts = useMemo(() => {
    const u = (k) => [...new Set(s.results.map((r) => r[k]).filter(Boolean))].sort();
    return { set: u("set_name"), rarity: u("rarity"), year: u("year").reverse() };
  }, [s.results]);
  const rows = s.results.filter((r) => (!f.set || r.set_name === f.set) && (!f.rarity || r.rarity === f.rarity) && (!f.year || r.year === f.year));
  const left = Math.max(0, (s.total || 0) - s.results.length);
  return (
    <div className="space-y-3" data-testid="p2-resultados">
      <SearchBox value={s.q} onChange={s.setQ} onClear={onClear} onScan={onScan} autoFocus testid="p2-search" />
      <div className="sticky z-20 -mx-4 px-4 py-2 bg-[#080808] flex flex-wrap gap-2" style={{ top }} data-testid="p2-filters">
        <button type="button" onClick={() => setF({ set: "", rarity: "", year: "" })} className={`h-8 px-3 rounded-full border text-xs font-bold ${!f.set && !f.rarity && !f.year ? "border-[#FF4438] text-white bg-[#FF4438]/15" : "border-white/10 text-white/70"}`} data-testid="p2-filter-all">Todos · {s.total || s.results.length}</button>
        <Filter label="Set" value={f.set} options={opts.set} onChange={(v) => setF({ ...f, set: v })} testid="p2-filter-set" />
        <Filter label="Rareza" value={f.rarity} options={opts.rarity} onChange={(v) => setF({ ...f, rarity: v })} testid="p2-filter-rarity" />
        <Filter label="Año" value={f.year} options={opts.year} onChange={(v) => setF({ ...f, year: v })} testid="p2-filter-year" />
      </div>
      {s.searching && !s.results.length && <HitSkeletonGrid rows={1} desktopCols={2} />}
      {s.error && <div className="text-[12px] text-[#FF6B5E]" data-testid="p2-error">{s.error} <button type="button" className="underline" onClick={s.retry}>Reintentar</button></div>}
      {s.searched && !s.searching && !s.results.length && <div className="text-[13px] text-white/55" data-testid="p2-empty">Sin resultados para "{s.q}". Prueba con el número o en inglés.</div>}
      <ul className="space-y-2">
        {rows.map((c, i) => <ResultRow key={c.id} c={c} game={game} onPick={onPick} best={i === 0 && !f.set && !f.rarity && !f.year}
          kbActive={kb && s.results[kb.activeIndex]?.id === c.id} kbVersion={kb?.activeVersion} />)}
      </ul>
      {s.enriching && <HitInlineLoader section="search" />}
      {s.hasMore && <button type="button" onClick={s.loadMore} disabled={s.loadingMore} className="w-full text-[13px] font-bold text-white py-2" data-testid="p2-more">{s.loadingMore ? "Cargando…" : `Ver ${left || ""} más ↓`}</button>}
      <div className="flex items-center justify-between gap-3 p-3.5 rounded-[16px] border border-dashed border-white/15" data-testid="p2-not-found">
        <div><div className="text-[14px] font-bold text-white">¿No está tu carta?</div><div className="text-[12px] text-white/55">Súbela igual. Se publica sola al aprobarse.</div></div>
        <button type="button" onClick={onNewCard} className="h-10 px-4 rounded-full border border-[#fbbf24] text-[#fbbf24] text-[13px] font-bold shrink-0" data-testid="p2-new-card">Carta nueva</button>
      </div>
    </div>
  );
};
